// src/utils/RecallPromptBuilder.ts

export type SubjectType =
  | "POLITY"
  | "ECONOMY"
  | "HISTORY_ANCIENT"
  | "HISTORY_MEDIEVAL"
  | "HISTORY_MODERN"
  | "ART_CULTURE"
  | "GEOGRAPHY"
  | "ENVIRONMENT"
  | "SCIENCE_TECH"
  | "IR"
  | "GENERAL";

export type SheetLength = "1_PAGE" | "2_PAGES" | "3_PAGES";

const getSubjectInstructions = (subject: SubjectType): string => {
  switch (subject) {
    case "POLITY":
      return `Focus: Articles, Case Laws, Bodies. Cue: "Article X" or "Case Name". Recall: Provision/Verdict.`;
    case "ECONOMY":
      return `Focus: Terms, Curves, Rates. Cue: "Term". Recall: Definition & Trend (e.g. "Rate Up -> Money Down"). NO LATEX.`;
    case "ART_CULTURE":
      return `Focus: Sites, Books, Styles. Cue: "Work/Site (Author/Loc)". Recall: Key Feature.`;
    case "HISTORY_ANCIENT":
    case "HISTORY_MEDIEVAL":
    case "HISTORY_MODERN":
      return `Focus: Terms, Battles, Treaty. Cue: "Term (Year)". Recall: Significance.`;
    case "ENVIRONMENT":
      return `Focus: Species, Acts. Cue: "Species (Status)". Recall: Habitat/Goal.`;
    case "SCIENCE_TECH":
      return `Focus: Missions, Tech. Cue: "Mission (Agency)". Recall: Objective.`;
    case "GEOGRAPHY":
      return `Focus: Landforms, Crops. Cue: "Feature". Recall: Condition/Location.`;
    default:
      return `Focus: Key Terms. Cue: Term. Recall: Definition.`;
  }
};

export const buildRecallPrompt = (
  text: string,
  subject: SubjectType,
  length: SheetLength,
  focusArea?: string
): string => {
  // Reduced counts to ensure they actually fit on the requested pages
  const quantityMap = {
    "1_PAGE": "15-18 high-yield points (STRICT LIMIT)",
    "2_PAGES": "30-35 points",
    "3_PAGES": "50-60 comprehensive points",
  };

  return `
*** SYSTEM INSTRUCTION: ACT AS A STRICT DATA EXTRACTOR ***
Create an "Active Recall Sheet" based ONLY on the User Notes provided.

=== SOURCE MATERIAL CONSTRAINT (CRITICAL) ===
1. **DO NOT** use outside knowledge. If a term is not in the text, DO NOT create a card for it.
2. **DO NOT** hallucinate definitions. Use the exact context from the notes.
3. If the user focuses on "${
    focusArea || "General"
  }", extract ONLY points related to that topic found IN THE TEXT.

=== SUBJECT: ${subject.replace("_", " & ")} ===
${getSubjectInstructions(subject)}

=== FORMATTING RULES ===
1. **NO LATEX MATH:** Use plain English (e.g. "Ratio of A to B").
2. **Language:** Concise English.
3. **Split:** Cue (Left) = Question/Term. Recall (Right) = Answer/Detail.

=== QUANTITY ===
Generate exactly ${quantityMap[length]}. 

=== OUTPUT FORMAT: JSON ONLY ===
[
  { 
    "cue": "Term from Notes", 
    "recall": "Definition found in notes", 
    "category": "Topic Sub-header" 
  }
]

=== USER NOTES ===
${text.substring(0, 25000)}
  `;
};
